// SourcesPage — CRM /sources admin page (#1080).
//
// @aiPurpose lets an admin (sources.manage) create/edit/archive Lead
//   sources, rotate their HMAC secret, and inspect delivery-log
//   summaries; a sources.read user sees the same data but no buttons.
// @aiSecurity the plaintext HMAC secret only escapes the wire via
//   `SourceSecretRevealModal`. Never store it on the source row.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadSource, LeadSourceSecretReveal } from '@magimp/shared';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Select,
  ToolbarActions,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import {
  useArchiveLeadSource,
  useLeadSourceDeliveryLogs,
  useRotateLeadSourceSecret,
} from '../queries.js';
import { useLeadSources } from '../../../catalog/index.js';
import { SourceFormModal } from './SourceFormModal.js';
import { SourceSecretRevealModal } from './SourceSecretRevealModal.js';

type EditTarget = { kind: 'create' } | { kind: 'edit'; source: LeadSource };

export function SourcesPage() {
  const { t } = useTranslation(['sources', 'common']);
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'sources.manage');
  const show = useToastStore((s) => s.show);

  const [includeArchived, setIncludeArchived] = useState(false);
  const [edit, setEdit] = useState<EditTarget | null>(null);
  const [reveal, setReveal] = useState<LeadSourceSecretReveal | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const list = useLeadSources(includeArchived ? { includeArchived: true } : {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{t('sources:title')}</h1>
        <ToolbarActions>
          <Select
            value={includeArchived ? 'all' : 'active'}
            onChange={(v) => setIncludeArchived(v === 'all')}
            options={[
              { value: 'active', label: t('sources:filters.active') },
              { value: 'all', label: t('sources:filters.all') },
            ]}
            style={{ width: 180 }}
          />
          {canManage && (
            <Button
              kind="primary"
              icon="plus"
              onClick={() => setEdit({ kind: 'create' })}
              data-testid="sources-create-button"
            >
              {t('sources:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>

      {list.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(list.error)}
          onRetry={() => void list.refetch()}
        />
      )}
      {list.isPending && <LoadingState />}
      {list.isSuccess && (
        <DataTable<LeadSource>
          rows={list.data}
          onSelect={(s) => setSelectedCode(s.code)}
          emptyState={
            <EmptyState title={t('sources:empty.title')} text={t('sources:empty.text')} />
          }
          columns={[
            {
              key: 'code',
              label: t('sources:columns.code'),
              render: (s) => <code data-testid={`source-row-${s.code}`}>{s.code}</code>,
            },
            { key: 'displayName', label: t('sources:columns.displayName') },
            {
              key: 'status',
              label: t('sources:columns.status'),
              render: (s) =>
                s.archivedAt
                  ? t('sources:status.archived')
                  : s.enabled
                    ? t('sources:status.enabled')
                    : t('sources:status.disabled'),
            },
            {
              key: 'secretIssuedAt',
              label: t('sources:columns.secretIssuedAt'),
              render: (s) => new Date(s.secretIssuedAt).toLocaleString(),
            },
            {
              key: 'actions',
              label: '',
              align: 'right',
              render: (s) =>
                canManage ? (
                  <SourceRowActions
                    source={s}
                    onEdit={() => setEdit({ kind: 'edit', source: s })}
                    onRotated={(r) => {
                      setReveal(r);
                      show({ tone: 'success', text: t('sources:toasts.rotated') });
                    }}
                    onArchived={() => show({ tone: 'success', text: t('sources:toasts.archived') })}
                    onError={(msg) => show({ tone: 'error', text: msg })}
                  />
                ) : null,
            },
          ]}
        />
      )}

      {selectedCode && (
        <SourceDeliveryLogsPanel code={selectedCode} onClose={() => setSelectedCode(null)} />
      )}

      <SourceFormModal
        open={edit != null}
        mode={edit?.kind === 'edit' ? 'edit' : 'create'}
        source={edit?.kind === 'edit' ? edit.source : undefined}
        onClose={() => setEdit(null)}
        onCreated={(r) => {
          setReveal(r);
          show({ tone: 'success', text: t('sources:toasts.created') });
        }}
        onUpdated={() => show({ tone: 'success', text: t('sources:toasts.updated') })}
      />
      <SourceSecretRevealModal
        open={reveal != null}
        reveal={reveal}
        onClose={() => setReveal(null)}
      />
    </div>
  );
}

interface SourceRowActionsProps {
  source: LeadSource;
  onEdit: () => void;
  onRotated: (reveal: LeadSourceSecretReveal) => void;
  onArchived: () => void;
  onError: (msg: string) => void;
}

function SourceRowActions({
  source,
  onEdit,
  onRotated,
  onArchived,
  onError,
}: SourceRowActionsProps) {
  const { t } = useTranslation(['sources', 'common']);
  const rotateMut = useRotateLeadSourceSecret(source.code);
  const archiveMut = useArchiveLeadSource(source.code);
  const isArchived = source.archivedAt != null;

  async function onRotate(): Promise<void> {
    if (!window.confirm(t('sources:confirm.rotate'))) return;
    try {
      const r = await rotateMut.mutateAsync();
      onRotated(r);
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  async function onArchive(): Promise<void> {
    if (!window.confirm(t('sources:confirm.archive'))) return;
    try {
      await archiveMut.mutateAsync();
      onArchived();
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  return (
    <div style={{ display: 'inline-flex', gap: 6 }}>
      <Button
        size="sm"
        onClick={onEdit}
        disabled={isArchived}
        data-testid={`source-edit-${source.code}`}
      >
        {t('common:actions.edit')}
      </Button>
      <Button
        size="sm"
        icon="rotate-ccw"
        onClick={() => {
          void onRotate();
        }}
        disabled={isArchived || rotateMut.isPending}
        data-testid={`source-rotate-${source.code}`}
      >
        {t('sources:actions.rotate')}
      </Button>
      <Button
        size="sm"
        icon="archive"
        onClick={() => {
          void onArchive();
        }}
        disabled={isArchived || archiveMut.isPending}
        data-testid={`source-archive-${source.code}`}
      >
        {t('sources:actions.archive')}
      </Button>
    </div>
  );
}

function SourceDeliveryLogsPanel({ code, onClose }: { code: string; onClose: () => void }) {
  const { t } = useTranslation(['sources', 'common']);
  const logs = useLeadSourceDeliveryLogs(code, { limit: 50 });

  return (
    <section
      aria-label={t('sources:logs.title', { code })}
      data-testid="source-delivery-logs"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 6,
        background: 'var(--bg-2)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          {t('sources:logs.title', { code })}
        </h2>
        <span style={{ flex: 1 }} />
        <Button size="sm" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </header>
      {logs.isPending && <LoadingState />}
      {logs.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(logs.error)}
          onRetry={() => void logs.refetch()}
        />
      )}
      {logs.isSuccess && logs.data.length === 0 && (
        <EmptyState title={t('sources:logs.empty.title')} text={t('sources:logs.empty.text')} />
      )}
      {logs.isSuccess && logs.data.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
          {logs.data.map((row) => (
            <li
              key={row.id}
              data-testid={`source-log-${row.id}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 110px 80px 1fr',
                gap: 8,
                fontSize: 12,
                padding: '6px 8px',
                background: 'var(--bg-1)',
                borderRadius: 4,
              }}
            >
              <span>{new Date(row.receivedAt).toLocaleString()}</span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{row.outcome}</span>
              <span>{row.httpStatus}</span>
              <span style={{ color: 'var(--fg-3)' }}>
                {row.leadId
                  ? t('sources:logs.lead', { id: row.leadId })
                  : row.idempotencyKey
                    ? t('sources:logs.idempotency', { key: row.idempotencyKey })
                    : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
